// Dimensions people make (design 02 3.2), as the device sees them: a list
// of items (TensorItem with role 'custom' and the dimension's dim_id) or,
// for an ordered dimension with a size, the whole numbers 1..size. Kind
// 'category' keeps its order for showing; 'ordered' is for shift, cumsum
// and diff. Formulas use the dimension by its name (grid/builtins.ts gives
// the engine its items).
import type { Id, TensorDim, TensorItem } from './api'

/** The living dimensions people made, in their order. */
export function customDims(dims: Record<number, TensorDim> | undefined): TensorDim[] {
  return Object.values(dims ?? {})
    .filter((d) => !d.deleted)
    .sort((a, b) => a.position - b.position || a.id - b.id)
}

/** The living items of a dimension people made, in order. */
export function dimItems(items: Record<number, TensorItem>, dimId: Id): TensorItem[] {
  return Object.values(items)
    .filter((i) => i.role === 'custom' && i.dim_id === dimId && !i.deleted)
    .sort((a, b) => a.position - b.position || a.id - b.id)
}

/** The names the engine gets for the dimension's items: "1".."size" for a range. */
export function dimItemNames(dim: TensorDim, items: Record<number, TensorItem>): string[] {
  if (dim.size !== null) return Array.from({ length: dim.size }, (_, i) => String(i + 1))
  return dimItems(items, dim.id).map((i) => i.name)
}

/** "범주", "순서", "순서 1..12" */
export function dimKindText(dim: Pick<TensorDim, 'kind' | 'size'>): string {
  if (dim.size !== null) return `순서 1..${dim.size}`
  return dim.kind === 'ordered' ? '순서' : '범주'
}

/** The names that are ours already (any case of ASCII letters, as the engine reads names). */
export const BUILTIN_NAMES = ['거래', '지출', '수입', '예산', '사람', '월', '분류', '결제수단'] as const

/** Words the formula reader takes as keywords after "(" or "," (over=, by=, ...). */
const KEYWORDS = ['over', 'by', 'along', 'horizon']

/**
 * What the app can say about a name for a dimension or tensor before the
 * server does (it also refuses function names, which only it knows):
 * null when it looks fine, else why not.
 */
export function identProblem(name: string, taken: Iterable<string> = []): string | null {
  const n = name.trim()
  if (n === '') return '이름을 적어 주세요.'
  if ([...n].length > 30) return '이름은 30자까지입니다.'
  if ((BUILTIN_NAMES as readonly string[]).includes(n)) return '미리 있는 이름이라 쓸 수 없어요.'
  if (KEYWORDS.includes(n.toLowerCase())) return '수식의 낱말(over, by, along, horizon)은 쓸 수 없어요.'
  if (!/^[\p{L}_\\][\p{L}\p{N}_.\\]*$/u.test(n)) return '글자로 시작하고 띄어쓰기·기호 없이 적어 주세요.'
  if (/^[A-Za-z]{1,3}[0-9]+$/.test(n) || /^[Rr][0-9]/.test(n) || /^[RrCc]$/.test(n)) return '칸 주소처럼 보이는 이름은 쓸 수 없어요.'
  const low = n.toLowerCase()
  for (const t of taken) if (t.toLowerCase() === low) return '이미 있는 이름입니다.'
  return null
}
